// App.js
import React, { useState, useEffect, useRef } from 'react';
import './App.css';

const Typewriter = ({ text, delay = 20, start = false }) => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // If start is false, do nothing (wait)
    if (!start) return;

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, delay, text, start]); // Add 'start' to dependencies

  return <span>{currentText}<span className="cursor-blink">|</span></span>;
};

// Helper to generate random styles for particles
const generateParticles = (count) => {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const size = Math.random() * 15 + 10;
    const style = {
      left: `${Math.random() * 100}%`,
      width: `${size}px`,
      height: `${size}px`,
      animationDuration: `${Math.random() * 20 + 15}s`,
      animationDelay: `${Math.random() * 20}s`,
    };

    // Randomly assign types: circle, square, or gold
    let className = 'particle';
    const randomType = Math.random();
    if (randomType > 0.6) className += ' square';
    else if (randomType > 0.3) className += ' gold';

    particles.push(<div key={i} className={className} style={style} />);
  }
  return particles;
};

/*  ProjectCard component in App.js */

const ProjectCard = ({ title, videoSrc, techStack }) => {
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    const video = videoRef.current;
    if (video) {
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {

        });
      }
    }
  };

  const handleMouseLeave = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <div
      className="project-window"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="window-header">
        <div className="window-dot red"></div>
        <div className="window-dot yellow"></div>
        <div className="window-dot green"></div>
        <div className="window-address-bar">{title}</div>
      </div>

      <div className="video-container">
        <video
          ref={videoRef}
          src={videoSrc}
          className="project-video"
          muted
          loop
          playsInline
        />

        <div className="project-overlay-static">
          <div className="play-icon">▶</div>
          <div className="tech-stack">
            {techStack.map((tech, index) => (
              <span key={index} className="tech-tag">{tech}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- SOCIAL ICONS (SVG PATHS) --- */
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

/*const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);*/

/* Project Showcase Icon */
const ProjectShowcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,11L9,14L12,17L15,14L12,11M7,13L8,14L7,15L6,14L7,13M17,13L18,14L17,15L16,14L17,13Z" />
  </svg>
);

/* Add this above function App() */
const LaptopDisplay = ({ videoSrc, projectUrl }) => {
  return (
    <div className="laptop-wrapper">
      <div className="laptop-screen-frame">
        <div className="laptop-camera"></div>
        <div className="laptop-screen-content">
          <video
            src={videoSrc}
            className="laptop-video"
            autoPlay
            muted
            loop
            playsInline
          />
          {/* Optional: A "Visit Site" overlay that appears on hover */}
          <a href={projectUrl} className="laptop-overlay">
            <span>View Case Study</span>
          </a>
        </div>
      </div>
      <div className="laptop-base"></div>
    </div>
  );
};

/* Add this above function App() */
const TiltCard = ({ children }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (window.innerWidth < 768) {
      return;
    }

    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;

    card.style.setProperty('--x', `${x}px`);
    card.style.setProperty('--y', `${y}px`);
  };

  const handleMouseLeave = () => {
    if (window.innerWidth < 768) {
      return;
    }

    const card = cardRef.current;
    card.style.transform = `perspective(1000px) rotateX(0) rotateY(0) scale(1)`;
  };

  return (
    <div
      ref={cardRef}
      className="tilt-card"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* The Shine Layer */}
      <div className="tilt-card-shine"></div>
      <div className="tilt-card-content">
        {children}
      </div>
    </div>
  );
};

/*  CodeEditor component in App() */
const CodeEditor = () => {
  return (
    <div className="code-window">
      <div className="window-header">
        <div className="window-dot red"></div>
        <div className="window-dot yellow"></div>
        <div className="window-dot green"></div>
        <div className="window-title">system_config.json</div>
      </div>
      <div className="code-content">
        <div className="line-numbers">
          <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
          <span>6</span><span>7</span><span>8</span><span>9</span><span>10</span>
          <span>11</span><span>12</span><span>13</span><span>14</span><span>15</span>
        </div>
        <pre className="code-text">
          <span className="c-func">{"{"}</span><br />
          &nbsp;&nbsp;<span className="c-arg">"identity"</span>: <span className="c-string">"Information Technology Undergraduate"</span>,<br />
          <br />
          &nbsp;&nbsp;<span className="c-arg">"philosophy"</span>: <span className="c-string">
            "I don't just write code — I design connected digital ecosystems"
          </span>,<br />
          <br />
          &nbsp;&nbsp;<span className="c-arg">"perspective"</span>: <span className="c-string">
            "Focused on understanding how systems communicate, from backend logic and databases to intuitive user interfaces"
          </span>,<br />
          <br />
          &nbsp;&nbsp;<span className="c-arg">"approach"</span>: <span className="c-string">
            "Combining technical precision with creative problem-solving to build seamless and efficient solutions"
          </span>,<br />
          <br />
          &nbsp;&nbsp;<span className="c-arg">"mission"</span>: <span className="c-string">
            "Creating scalable, real-world digital systems that balance performance, usability, and maintainability"
          </span><br />
          <span className="c-func">{"}"}</span>
        </pre>

      </div>
    </div>
  );
};

/*  Reactor component in App() */
const Reactor = () => {
  return (
    <div className="reactor-container">
      <div className="reactor-ring ring-1"></div>
      <div className="reactor-ring ring-2"></div>
      <div className="reactor-ring ring-3"></div>
      <div className="reactor-ring ring-4"></div>
      <div className="reactor-core"></div>
      <div className="reactor-glow"></div>
    </div>
  );
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeSection, setActiveSection] = useState('home');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [particleCount, setParticleCount] = useState(35);

  /* --- NEW PAGINATION STATE --- */
  const [currentPage, setCurrentPage] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const projectsPerPage = 6; // 2 rows of 3

  /* --- PROJECTS DATA ARRAY --- */
  const allProjects = [
    {
      title: "RentAVehicle - Ad Platform",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100510/Vehicles_gucyap.mp4",
      techStack: ['Java', 'MySQL', 'Tomcat']
    },
    {
      title: "Workflows Eng - Home",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100510/WorkFlowsFE_hjugh0.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "Ink & Pens - Bookstore",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1770230250/inkandpen_rajodq.mp4",
      techStack: ['React', 'ASP.NET', 'SQL']
    },
    {
      title: "Auram - Jewelry e-commerce",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768298829/Auram_x2gtas.mp4",
      techStack: ['React Vite', 'JavaScript', 'Tailwind CSS']
    },
    {
      title: "WorkFlows ENG - Projects",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100513/WProjects_n2cce4.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "HOTELMANIA - Home",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768042733/HotelMania_Home_loauh8.mp4",
      techStack: ['React ROLLDOWN-VITE', 'Node.js', 'MongoDB']
    },
    {
      title: "HOTELMANIA - Rooms",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768042733/HotelMania_Rooms_lyp16c.mp4",
      techStack: ['React ROLLDOWN-VITE', 'Node.js', 'MongoDB']
    },
    {
      title: "HOTELMANIA - Dashboards",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768042732/HotelMania_Dashboard_x6ajp4.mp4",
      techStack: ['React ROLLDOWN-VITE', 'Node.js', 'MongoDB']
    },
    {
      title: "RentalDay - User",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768231032/rentalday_wu_c9avhh.mp4",
      techStack: ['React ROLLDOWN-VITE', 'Node.js', 'MongoDB']
    },
    {
      title: "RentalDay - Admin",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768231033/rentalday_uad_us1sgo.mp4",
      techStack: ['React ROLLDOWN-VITE', 'Node.js', 'MongoDB']
    },
    {
      title: "Timelines - Dashboard",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100509/Timeline_n2iuzs.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "Financial - Dashboard",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100508/FDashboard_cijbpy.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "Notifications - Dashboard",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1768208056/notification_h0kkse.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "WorkFlows ENG - Admin",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100511/WorkFlowsAD_p3r4na.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "Projecto AI - Chatbot",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767100506/ChatBot_oy12nl.mp4",
      techStack: ['React', 'Node.js', 'MongoDB']
    },
    {
      title: "TourDay - Home",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767616106/TourDay_xvyyde.mp4",
      techStack: ['React ROLLDOWN-VITE', 'Node.js', 'MySQL']
    },
    {
      title: "MindOfMine - Mood Tracker",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767108000/MindIfI_Kotlin_mypvlw.mp4",
      techStack: ['Kotlin', 'Android Studio', 'SQLite']
    },
    {
      title: "Workout Tracker ",
      videoSrc: "https://res.cloudinary.com/dc0cujoji/video/upload/v1767617667/Screen_recording_20260105_182003_fxayjx.mp4",
      techStack: ['Java', 'Android Studio', 'SQLite']
    },
  ];

  // Logic for Pagination
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = allProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(allProjects.length / projectsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber === currentPage) return;

    setIsPageLoading(true);
    setTimeout(() => {
      setCurrentPage(pageNumber);
      setIsPageLoading(false);
    }, 300);
  };

  const sectionRefs = useRef({});

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = 'auto';
          }, 500);
          return 100;
        }
        return prev + 1;
      });
    }, 20);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
        setParticleCount(35);
      } else {
        setParticleCount(15);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const updateMousePosition = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', updateMousePosition);
    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.5,
      rootMargin: '0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set(prev).add(entry.target.id));

          const navIds = ['home', 'about', 'education', 'experience', 'projects', 'contact'];
          if (navIds.includes(entry.target.id)) {
            setActiveSection(entry.target.id);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id) => (ref) => {
    sectionRefs.current[id] = ref;
  };

  return (
    <>
      {/* PRELOADER */}
      <div className={`preloader ${!loading ? 'fade-out' : ''}`}>
        <div className="loader-content">
          <span className="loader-text">INITIALIZING PORTFOLIO</span>
          <span className="loader-count">{count}%</span>
          <div className="loader-bar">
            <div className="loader-progress" style={{ width: `${count}%` }}></div>
          </div>
        </div>
      </div>

      <div className="App">

        {/* --- LIVE BACKGROUND --- */}
        <div className="background-animation">
          {generateParticles(particleCount)} {/* Generates particles based on screen size */}
        </div>
        {/* ----------------------- */}

        <div
          className="custom-cursor"
          style={{ left: `${mousePosition.x}px`, top: `${mousePosition.y}px` }}
        />

        {/* Enhanced Navigation */}
        <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
          <div className="nav-container">
            <div className="nav-logo">KAVISHKA RAVISHAN</div>

            <div className={`nav-links ${isMenuOpen ? 'mobile-menu-open' : ''}`}>
              <a
                href="#home"
                className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}
                onClick={(e) => {
                  setActiveSection('home');
                  setIsMenuOpen(false);
                }}
              >
                HOME
              </a>
              <a
                href="#about"
                className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
                onClick={(e) => {
                  setActiveSection('about');
                  setIsMenuOpen(false);
                }}
              >
                ABOUT
              </a>
              <a
                href="#education"
                className={`nav-link ${activeSection === 'education' ? 'active' : ''}`}
                onClick={(e) => {
                  setActiveSection('education');
                  setIsMenuOpen(false);
                }}
              >
                EDUCATION
              </a>
              <a
                href="#experience"
                className={`nav-link ${activeSection === 'experience' ? 'active' : ''}`}
                onClick={(e) => {
                  setActiveSection('experience');
                  setIsMenuOpen(false);
                }}
              >
                EXPERIENCE
              </a>
              <a
                href="#projects"
                className={`nav-link ${activeSection === 'projects' ? 'active' : ''}`}
                onClick={(e) => {
                  setActiveSection('projects');
                  setIsMenuOpen(false);
                }}
              >
                PROJECTS
              </a>
              <a
                href="#contact"
                className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                onClick={(e) => {
                  setActiveSection('contact');
                  setIsMenuOpen(false);
                }}
              >
                CONTACT
              </a>
            </div>

            {/* Hamburger Menu Icon */}
            <div
              className="hamburger-menu"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className={`hamburger-line ${isMenuOpen ? 'line-1-open' : ''}`}></span>
              <span className={`hamburger-line ${isMenuOpen ? 'line-2-open' : ''}`}></span>
              <span className={`hamburger-line ${isMenuOpen ? 'line-3-open' : ''}`}></span>
            </div>

            <div className="nav-social">
              <a href="https://linkedin.com/in/kavishka-ranamukage-4869663a5" target="_blank" rel="noopener noreferrer" className="social-icon">
                <LinkedInIcon />
              </a>
              <a href="https://github.com/ShanthanosJr" target="_blank" rel="noopener noreferrer" className="social-icon">
                <GitHubIcon />
              </a>
              <a href="https://projects-showcase-xi.vercel.app/" target="_blank" rel="noopener noreferrer" className="social-icon">
                <ProjectShowcaseIcon />
              </a>
            </div>

          </div>
        </nav>


        {/* ==================== THE "3D POP" CENTRAL HERO ==================== */}
        <section
          id="home"
          ref={setSectionRef('home')}
          className={`hero-central-stage ${visibleSections.has('home') && !loading ? 'hero-animate' : ''}`}        >
          {/* 1. The Background Massive Title (Sitting behind image) */}
          <div className="central-title-layer">
            <h2 className="central-subtitle">INFORMATION TECHNOLOGY</h2>
            <h1 className="central-main-title">PORTFOLIO</h1>
          </div>

          {/* 2. The Central 3D Pop-up Image (Sitting on top) */}
          <div className="central-image-layer">
            <div className="image-glow-center"></div>
            <img
              src="https://res.cloudinary.com/dc0cujoji/image/upload/v1768130523/HP2_tbjig9.png"
              alt="Kavishka Ravishan"
              className="pop-up-3d-image"
            />
          </div>

          {/* 3. Left Side Text: "Creative Presentation" */}
          <div className="flanking-text-block left-block">
            <span className="flank-label">CREATIVE</span>
            <h3 className="flank-title">PORTFOLIO<br />PRESENTATION</h3>
            <div className="flank-line"></div>
          </div>

          {/* 4. Right Side Text: "Presented By" */}
          <div className="flanking-text-block right-block">
            <span className="flank-label align-right">PRESENTED BY</span>
            <h3 className="hero-signature right-sig">Kavishka Ravishan</h3>
            <div className="flank-line align-right"></div>
          </div>

          {/* 5. Bottom Left Loading Dots */}
          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>

        </section>
        {/* =============================================================== */}

        {/* WELCOME / MISSION CONTROL SECTION */}
        <section
          id="about"
          ref={setSectionRef('about')}
          className={`section-split ${visibleSections.has('about') ? 'animate-slide-left' : ''}`}
        >
          <div className="container-split">

            {/* Left Side: The HUD Interface */}
            <div className="split-content hud-panel">
              <div className="hud-header">
                <span className="hud-title">SYSTEM_MESSAGE_001</span>
                <span className="hud-status">● PRIORITY: HIGH</span>
              </div>

              <h2 className="section-title-large">
                WELCOME TO <br />
                <span className="gold-text">MY PORTFOLIO</span>
              </h2>

              <div className="hud-body">
                <p className="section-text">
                  I am a <span className="highlight-text">Information Technology Specialist</span> and <span className="highlight-text">IT Strategist</span>.
                  My mission is not just to write code, but to engineer the intelligence behind the interface.
                </p>
                <p className="section-text">
                  From the precision of <strong>backend logic</strong> to the fluidity of <strong>frontend design</strong>,
                  I build systems that bridge the gap between complex data and human experience.
                </p>
              </div>

              <div className="hud-footer">
                <div className="hud-stat">
                  <span className="stat-label">EXP</span>
                  <span className="stat-val">3+ YRS</span>
                </div>
                <div className="hud-stat">
                  <span className="stat-label">PROJECTS</span>
                  <span className="stat-val">10+</span>
                </div>
                {/* REPLACE THE BUTTON WITH THIS LINK */}
                <a
                  href="assets/KavishkaRavishan_Resume.pdf"
                  download="Kavishka_Ravishan_Resume.pdf"
                  className="btn-primary hud-btn"
                  style={{ textDecoration: 'none' }}
                >
                  INITIALIZE CV <span>→</span>
                </a>
              </div>

              {/* Decorative Corners */}
              <div className="corner c-tl"></div>
              <div className="corner c-tr"></div>
              <div className="corner c-bl"></div>
              <div className="corner c-br"></div>
            </div>

            {/* Right Side: The Cyber Reactor */}
            <div className="split-image reactor-wrapper">
              <Reactor />
            </div>

          </div>

          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Introduction Section - Fade Up */}
        <section
          id="introduction"
          ref={setSectionRef('introduction')}
          className={`section-center ${visibleSections.has('introduction') ? 'animate-fade-up' : ''}`}
        >
          <div className="intro-scanner-wrapper">
            {/* Scanner 1 */}
            <div className="scanner-port">
              <div className="scanner-ring-outer"></div>
              <div className="scanner-ring-inner"></div>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                alt="Data Core"
                className="scanner-img"
              />
              <div className="scanner-line"></div>
              <div className="scanner-label">DATA_CORE</div>
            </div>

            {/* Scanner 2 */}
            <div className="scanner-port delay-scan">
              <div className="scanner-ring-outer reverse"></div>
              <div className="scanner-ring-inner"></div>
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                alt="Logic Core"
                className="scanner-img"
              />
              <div className="scanner-line"></div>
              <div className="scanner-label">LOGIC_CORE</div>
            </div>
          </div>
          <div className="intro-content">
            <h2 className="section-title">INTRO<span className="gold-text">DUCTION</span></h2>
            <p className="section-text">
              As an <span className="gold-text">Information Technology</span> student,
              <Typewriter text=" I don't just write code—I build ecosystems. My passion lies in understanding how digital systems connect, 
              from the backend database to the user interface." delay={30} start={visibleSections.has('introduction')} />
            </p>
            <CodeEditor />
          </div>
          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* About Me Section - Slide From Right */}
        <section
          id="about-me"
          ref={setSectionRef('about-me')}
          className={`section-split ${visibleSections.has('about-me') ? 'animate-slide-right' : ''}`}
        >
          <div className="container-split">
            <div className="split-content">
              <h2 className="section-title">ABOUT <span className="gold-text">ME</span></h2>
              <p className="section-text">
                <Typewriter text="Undergraduate Student | SLIIT — 3rd Year BSc (Hons) in Information Technology. Currently studying network modules, software engineering modules, data structures and algorithms modules, along with general Information Technology modules. Maintained consistently above 3.0 GPA throughout university journey. Building a versatile skill set spanning software engineering, databases, networking, and system security. Experienced through academic projects and responsibilities in a family-owned business (Sethsiri Hardware) as a Junior Accountant. Background in Engineering Technology at Thurstan College, developing adaptability, resilience, and self-learning.
                " delay={20} start={visibleSections.has('about-me')} />
              </p>
              <button
                className="btn-primary"
                onClick={() => window.open('https://projects-showcase-xi.vercel.app/', '_blank', 'noopener,noreferrer')}
              >
                More About Me<span>→</span>
              </button>
            </div>
            {/*<div className="split-image">
            <img
              src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="About Me"
              className="image-rounded"
            />
          </div>*/}
            <div className="data-skill-container">
              <h3 className="skill-header">SKILL PROFICIENCY DATA</h3>

              {[
                "React / Node.js / Express.js / RESTful APIs",
                "HTML / CSS / Tailwind CSS / Bootstrap / Bulma",
                "MongoDB / MySQL / SQL / NoSQL / Oracle",
                "Java / Spring Boot / Backend Development",
                "PHP / WordPress / Apache",
                "Kotlin / Java / Android App Development",
                "Python / R / Data Analysis",
                "C / C++ / C# / .NET Fundamentals / Data Structures",
                "Git / Version Control / GitHub",
                "UI|UX Design / Figma / Canva / Responsive Design",
              ].map((skill, index) => (
                <div key={index} className="skill-bar-wrapper">
                  <div className="skill-info">
                    <span className="skill-name">{skill}</span>
                    <span className="skill-pct">
                      {/* Random numbers to look like live data */}
                      {Math.floor(Math.random() * (99 - 85) + 85)}%
                    </span>
                  </div>
                  <div className="skill-track">
                    <div
                      className="skill-fill"
                      style={{
                        width: visibleSections.has('about-me') ? `${Math.random() * (95 - 80) + 80}%` : '0%'
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Education Section - Scale Up */}
        <section
          id="education"
          ref={setSectionRef('education')}
          className={`section-center ${visibleSections.has('education') ? 'animate-scale-up' : ''}`}
        >
          <h2 className="section-title">EDU<span className="gold-text">CATION</span></h2>
          <div className="cine-viewport">
            <img
              src="https://res.cloudinary.com/dc0cujoji/image/upload/v1768231331/AiEd_qixrt9.png"
              alt="Education Journey"
              className="cine-img"
            />
            <div className="cine-overlay"></div>
            <div className="cine-ui-top">REC ●</div>
            <div className="cine-ui-bottom">
              <span>ISO 800</span>
              <span>1/50</span>
              <span>4K RAW</span>
            </div>
          </div>
          <div className="grid-3">
            <TiltCard>
              <div className="card-year">
                <div className="year-line"></div>
                <span className="year-text">2008 - 2019</span>
              </div>
              <h3 className="card-title">Kottawa Ananda College</h3>
              <p className="card-text">
                Completed O/L exams. Studied ICT, Business management and Music streams and developed foundational knowledge in Business management, Mathematics, Science, and Music. Built academic discipline and study habits that prepared me for advanced studies.
              </p>
            </TiltCard>
            <TiltCard>
              <div className="card-year">
                <div className="year-line"></div>
                <span className="year-text">2020 - 2023</span>
              </div>
              <h3 className="card-title">Thurstan College</h3>
              <p className="card-text">
                Completed A/L exams in Engineering Technology stream with ICT in addition. Studied Engineering Technology, mathematics, physics, and Science subjects with practical skills. Developed resilience, discipline, and independent learning skills during remote and hybrid learning periods.
              </p>
            </TiltCard>
            <TiltCard>
              <div className="card-year">
                <div className="year-line"></div>
                <span className="year-text">2023 - Present</span>
              </div>
              <h3 className="card-title">SLIIT University</h3>
              <p className="card-text">
                Undergraduate student enrolled in the BSc (Hons) Information Technology program. Gaining holistic knowledge in software development, information systems, and network infrastructure. Developing comprehensive IT problem-solving skills.
              </p>
            </TiltCard>
          </div>
          {/*<div className="section-dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>*/}
          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Work Experience Section - Flip In */}
        <section
          id="experience"
          ref={setSectionRef('experience')}
          className={`section-center ${visibleSections.has('experience') ? 'animate-flip-in' : ''}`}
        >
          <h2 className="section-title">WORK <span className="gold-text">EXPERIENCE</span></h2>
          <div className="grid-3">
            <TiltCard>
              <div className="card-year">
                <div className="year-line"></div>
                <span className="year-text">2020</span>
              </div>
              <h3 className="card-title">Sethsiri Hardware</h3>
              <p className="card-text">
                Junior Accountant (Part-Time) - Assisted financial operations, record maintenance, cash handling, and basic accounting tasks in the family business. Worked under the guidance of experts and gained early exposure to business finance and operations.
              </p>
            </TiltCard>
            <TiltCard>
              <div className="card-year">
                <div className="year-line"></div>
                <span className="year-text">2023 - Present</span>
              </div>
              <h3 className="card-title">SLIIT University</h3>
              <p className="card-text">
                Academic Projects - Working on various full-stack & system development projects as part of undergraduate studies, gaining expertise in General Information Technology and Enterprise Systems.
              </p>
            </TiltCard>
            <TiltCard>
              <div className="card-year">
                <div className="year-line"></div>
                <span className="year-text">2018 - 2019</span>
              </div>
              <h3 className="card-title">Thurstan College</h3>
              <p className="card-text">
                Engineering Technology Student - Studied Engineering Technology, mathematics, physics, and Science subjects with practical skills. Developed resilience, discipline, and independent learning skills during remote and hybrid learning periods.
              </p>
            </TiltCard>
          </div>
          <div className="grid-2">
            <div className="holo-frame">
              <img
                src="https://res.cloudinary.com/dc0cujoji/image/upload/v1768231334/sliit_lsm6j7.png"
                alt="Studio"
                className="holo-img"
              />
              <div className="holo-grid-overlay"></div>
              <div className="holo-border"></div>
              <span className="holo-tag">ARCHIVE_01</span>
            </div>

            <div className="holo-frame">
              <img
                src="https://res.cloudinary.com/dc0cujoji/image/upload/v1768231333/tc_h4o7yw.png"
                alt="Workspace"
                className="holo-img"
              />
              <div className="holo-grid-overlay"></div>
              <div className="holo-border"></div>
              <span className="holo-tag">ARCHIVE_02</span>
            </div>
          </div>
          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Project Portfolio Section */}
        <section
          id="projects"
          ref={setSectionRef('projects')}
          className={`section-center ${visibleSections.has('projects') ? 'animate-bounce-in' : ''}`}
        >
          <h2 className="section-title-xl">PROJECTS <span className="gold-text">PORTFOLIO</span></h2>
          <p className="section-text centered wide">
            <Typewriter text="A curated selection of my academic and personal projects, showcasing technical skills, analytical thinking, and a broad competency in Information Technology and System Engineering." delay={40} start={visibleSections.has('projects')} />
          </p>

          {/* DYNAMIC PROJECT GRID */}
          <div className={`grid-3 ${isPageLoading ? "loading-transition" : ""}`}>
            {currentProjects.map((project, index) => (
              <ProjectCard
                key={`${currentPage}-${index}`}
                title={project.title}
                videoSrc={project.videoSrc}
                techStack={project.techStack}
              />
            ))}
          </div>

          {/* PAGINATION CONTROLS */}
          {totalPages > 1 && (
            <div className="pagination-container">
              {/* Previous Button */}
              <button
                className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''} ${isPageLoading ? 'loading' : ''}`}
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || isPageLoading}
              >
                {isPageLoading && currentPage === 1 ? <span className="loading-dots">●●●</span> : <>&lt; PREV</>}
              </button>

              {/* Page Numbers */}
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`pagination-btn number ${currentPage === i + 1 ? 'active' : ''} ${isPageLoading ? 'loading' : ''}`}
                  onClick={() => paginate(i + 1)}
                  disabled={isPageLoading}
                >
                  {isPageLoading && currentPage === i + 1 ? <span className="loading-dots">●●●</span> : i + 1}
                </button>
              ))}

              {/* Next Button */}
              <button
                className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''} ${isPageLoading ? 'loading' : ''}`}
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || isPageLoading}
              >
                {isPageLoading && currentPage === totalPages ? <span className="loading-dots">●●●</span> : <>NEXT &gt;</>}
              </button>
            </div>
          )}

          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Latest Project Section - Rotate In */}
        <section
          id="latest-project"
          ref={setSectionRef('latest-project')}
          className={`section-center ${visibleSections.has('latest-project') ? 'animate-rotate-in' : ''}`}
        >
          <div className="latest-project-container">
            <h2 className="section-title">FEATURED <span className="gold-text">WORK</span></h2>

            <div className="latest-project-layout">
              {/* Left Side: Details */}
              <div className="project-info-panel">
                <h3 className="highlight-title">Logistics Corp <span className="gold-text">Inventory System</span></h3>
                <p className="section-text">
                  <Typewriter text="A comprehensive online inventory system connecting logistics corporations with suppliers and customers. Features real-time bidding, analytics dashboards, and automated payment processing. To view more project details, click the button below." delay={40} start={visibleSections.has('latest-project')} />
                </p>

                {/* Holographic Tech Badge */}
                <div className="tech-badge-container">
                  <div className="tech-badge">
                    <span className="badge-label">Framework</span>
                    <span className="badge-value">Springboot / React js </span>
                  </div>
                  <div className="tech-badge">
                    <span className="badge-label">DATABASE</span>
                    <span className="badge-value">MySQL</span>
                  </div>
                  <div className="tech-badge">
                    <span className="badge-label">STATUS</span>
                    <span className="badge-value live">
                      <a
                        href="https://logistics-corp-ms.vercel.app/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ● Check Deployment
                      </a>
                    </span>
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  style={{ marginTop: '2rem' }}
                  onClick={() => window.open('https://projects-showcase-xi.vercel.app/', '_blank')}
                >
                  View All Case Studies <span className="arrow-icon">→</span>
                </button>

              </div>

              {/* Right Side: The Laptop */}
              <div className="project-visual-panel">
                <LaptopDisplay
                  videoSrc="https://res.cloudinary.com/dc0cujoji/video/upload/v1768629628/Inventory_tlb96v_e638c6.mp4"
                  projectUrl="https://logistics-corp-ms.vercel.app/"
                />
              </div>
            </div>
          </div>

          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Contact Section - Slide Up */}
        <section
          id="contact"
          ref={setSectionRef('contact')}
          className={`section-split ${visibleSections.has('contact') ? 'animate-slide-up' : ''}`}
        >
          <div className="container-split">
            {/* Left: Encrypted Comms Panel */}
            <div className="split-content">
              <h2 className="section-title">ESTABLISH<br /><span className="gold-text">UPLINK</span></h2>
              <p className="section-text">
                <Typewriter text="Available for freelance contracts, full-time opportunities, or technical collaboration. Transmission channels are open." delay={40} start={visibleSections.has('contact')} />
              </p>

              <div className="contact-grid">
                {/* Email Card */}
                <a href="mailto:kavishkaravishan544@gmail.com" className="comms-card">
                  <div className="comms-icon-box">@</div>
                  <div className="comms-details">
                    <span className="comms-label">DIRECT_MSG // EMAIL</span>
                    <span className="comms-value">kavishkaravishan544@gmail.com</span>
                  </div>
                </a>

                {/* Phone Card */}
                <a href="tel:0714298544" className="comms-card">
                  <div className="comms-icon-box">☎</div>
                  <div className="comms-details">
                    <span className="comms-label">VOICE_LINK // PHONE</span>
                    <span className="comms-value">+94 71 429 8544</span>
                  </div>
                </a>

                {/* Location Card */}
                <div className="comms-card">
                  <div className="comms-icon-box">📍</div>
                  <div className="comms-details">
                    <span className="comms-label">GEO_COORDS // BASE</span>
                    <span className="comms-value">Kottawa, Panipitiya, Sri Lanka</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Holographic Projector */}
            <div className="split-image">
              <div className="holo-projector">
                <img
                  src="https://res.cloudinary.com/dc0cujoji/image/upload/v1768231332/AiContact_ua53qg.png"
                  alt="Holographic Transmission"
                  className="holo-image"
                />
                <div className="holo-scanline"></div>
                <div className="holo-base"></div>
              </div>
            </div>
          </div>

          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>

        {/* Thank You Section - Session Terminated */}
        <section
          id="thank-you"
          ref={setSectionRef('thank-you')}
          className={`section-center ${visibleSections.has('thank-you') ? 'animate-fade-zoom' : ''}`}
        >
          <h1 className="section-title-xl">SESSION<br /><span className="gold-text">COMPLETE</span></h1>

          <div className="thank-you-container">
            {/* The Digital ID Card */}
            <div className="digital-id-card">
              <div className="id-header">
                <span className="id-logo">IT // PORTFOLIO</span>
                <div className="id-chip"></div>
              </div>
              <div className="id-body">
                <span className="id-name">KAVISHKA RAVISHAN</span>
                <span className="id-role">INFORMATION TECHNOLOGY SPECIALIST</span>
              </div>
              <div style={{ fontSize: '0.6rem', color: '#444', marginTop: '10px' }}>
                SLIIT ID: IT23641624
              </div>
            </div>

            <p className="section-text centered wide">
              <Typewriter text="Thank you for accessing my digital portfolio. The system is ready for new challenges and collaborations." delay={40} start={visibleSections.has('thank-you')} />
            </p>

            {/* Reboot System Button */}
            <button
              className="btn-reboot"
              onClick={() => {
                window.location.href = '/';

                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              }}
            >
              <span>▲</span> RE-INITIALIZE PORTFOLIO
            </button>

          </div>

          <footer className="system-footer">
            <p className="footer-text">
              © 2026 KAVISHKA RAVISHAN | PORTFOLIO SYSTEM  | Made With React.js
            </p>
          </footer>

          <div className="hero-loading-dots-corner">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </section>
      </div>
    </>
  );
}

export default App;